// ============================================================================
// Logistics leg drawer — every truck booking we hold for one leg of the Route.
// A leg can carry more than one (a superseded booking plus its replacement), so
// this stacks them; each renders through the shared <BookingDetails>, the same
// rendering the Logistics dashboard shows for a row.
// ============================================================================
import { Button, Divider, Drawer, Empty, Tag, Typography, message } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import React from "react";
import BookingDetails, { fromDocument } from "./booking_details";
import { LogisticsDocument } from "../types";
import { getLoginextTrackingUrl } from "../logistics/logistics.api";

interface Props {
  open: boolean;
  onClose: () => void;
  bookings: LogisticsDocument[];
  title?: string;
}

const LogisticsDrawer: React.FC<Props> = ({ open, onClose, bookings, title }) => {
  const [loading, setLoading] = React.useState(false);

  const activeBooking = bookings.find((b) => b.is_active) || bookings[0];
  const orderNo = activeBooking?.provider_ref;
  const isDelhivery = activeBooking?.provider_type === "delhivery";
  // Delhivery carries its tracking/dashboard URL right on the booking; LogiNext is resolved live.
  const delhiveryUrl =
    activeBooking?.tms_order_url ||
    activeBooking?.tracking_link ||
    activeBooking?.tracking_url ||
    null;

  const openInTms = async () => {
    if (isDelhivery) {
      if (delhiveryUrl) window.open(delhiveryUrl, "_blank", "noopener,noreferrer");
      else message.info("No Delhivery tracking link available for this booking");
      return;
    }
    if (!orderNo) return;
    setLoading(true);
    const url = await getLoginextTrackingUrl(orderNo);
    setLoading(false);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Show the button when there's something to open: a Delhivery URL, or a LogiNext order ref to resolve.
  const canOpen = isDelhivery ? !!delhiveryUrl : !!orderNo;

  return (
    <Drawer
      title={title || "Logistics details"}
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      extra={
        canOpen ? (
          <Button
            type="primary"
            icon={<ExportOutlined />}
            loading={loading}
            onClick={openInTms}
          >
            {isDelhivery ? "Open in Delhivery" : "Open in LogiNext"}
          </Button>
        ) : null
      }
    >
    {bookings.length === 0 && <Empty description="No truck booked for this leg" />}
    {/* Newest booking first: a leg stacks a superseded booking + its replacement(s), and the
        current/latest one should lead. Backend returns them ascending by id, so sort descending. */}
    {[...bookings].sort((a, b) => b.id - a.id).map((b, i) => (
      <div key={b.id} style={{ marginBottom: 28 }}>
        {i > 0 && <Divider />}
        <Typography.Title level={5} style={{ marginBottom: 8 }}>
          Booking #{b.id}
          {b.is_active ? (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              current
            </Tag>
          ) : (
            <Tag style={{ marginLeft: 8 }}>replaced</Tag>
          )}
        </Typography.Title>
        <BookingDetails view={fromDocument(b)} adminOnlyProofs />
      </div>
    ))}
    </Drawer>
  );
};

export default LogisticsDrawer;
